import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { X } from "lucide-react";

const STORAGE_KEY = "plainly.cookie-consent.v1";
const GA_ID = process.env.REACT_APP_GA_MEASUREMENT_ID;

/**
 * Cookie / data-use consent banner.
 *
 * Stored choice (localStorage):
 *   { choice: "all" | "necessary", at: ISOString }
 *
 * - "necessary" → only the session_token auth cookie is used. No analytics.
 * - "all"       → above PLUS Google Analytics 4 (anonymised IP, no ads, no
 *                 cross-site personalisation). Only fired when GA_ID is set.
 *
 * Listen for the custom event 'plainly:cookie-consent:open' (window.dispatchEvent) to
 * re-open the banner from anywhere (e.g. footer "Cookies" link).
 *
 * Fires 'plainly:cookie-consent:changed' whenever the choice is recorded — the
 * GoogleAnalytics loader subscribes to this to start tracking immediately on accept.
 */
export default function CookieConsent() {
  const [visible, setVisible] = useState(false);

  const open = useCallback(() => setVisible(true), []);
  const close = useCallback(() => setVisible(false), []);

  useEffect(() => {
    let stored = null;
    try { stored = localStorage.getItem(STORAGE_KEY); } catch {}
    if (!stored) setVisible(true);
  }, []);

  useEffect(() => {
    const handler = () => open();
    window.addEventListener("plainly:cookie-consent:open", handler);
    return () => window.removeEventListener("plainly:cookie-consent:open", handler);
  }, [open]);

  const record = (choice) => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ choice, at: new Date().toISOString() }),
      );
    } catch {}
    window.dispatchEvent(new Event("plainly:cookie-consent:changed"));
    close();
  };

  if (!visible) return null;

  // Copy adapts to whether GA is actually configured.
  const analyticsLine = GA_ID
    ? "Plainly uses a single essential cookie to keep you signed in. With your consent, we also use Google Analytics to understand how visitors find the site — anonymised IP, no ads, no cross-site tracking."
    : "Plainly only uses a single essential cookie to keep you signed in. We don't use marketing or third-party tracking cookies.";

  return (
    <div
      className="no-print fixed inset-x-3 bottom-3 sm:inset-x-auto sm:left-6 sm:bottom-6 sm:max-w-md z-50"
      role="dialog"
      aria-live="polite"
      aria-label="Cookie preferences"
      data-testid="cookie-consent-banner"
    >
      <div className="rounded-2xl bg-white border border-black/10 shadow-2xl p-5 relative">
        <button
          onClick={() => record("necessary")}
          aria-label="Dismiss"
          className="absolute top-3 right-3 p-1 rounded-full hover:bg-[#F7F5F0] text-[#1E392A]/50"
          data-testid="cookie-consent-close"
        >
          <X className="w-4 h-4" />
        </button>
        <div className="tag-label text-[#E27D60]">Cookies & data</div>
        <div className="font-display text-lg mt-1.5 pr-6">A quick heads-up on what we store.</div>
        <p className="text-sm text-[#1E392A]/75 mt-2 leading-relaxed">
          {analyticsLine}{" "}
          <Link to="/privacy" className="underline" data-testid="cookie-consent-privacy-link">See our Privacy Policy</Link>{" "}
          for the full story.
        </p>
        <div className="mt-4 flex flex-wrap gap-2 justify-end">
          <button
            onClick={() => record("necessary")}
            className="text-sm rounded-full h-9 px-4 border border-[#1E392A]/15 hover:bg-[#F7F5F0]"
            data-testid="cookie-consent-necessary-btn"
          >
            Necessary only
          </button>
          <button
            onClick={() => record("all")}
            className="text-sm rounded-full h-9 px-4 bg-[#1E392A] hover:bg-[#152b20] text-white font-medium"
            data-testid="cookie-consent-accept-btn"
          >
            {GA_ID ? "Accept analytics" : "Got it"}
          </button>
        </div>
      </div>
    </div>
  );
}

export function openCookieConsent() {
  window.dispatchEvent(new Event("plainly:cookie-consent:open"));
}
